import { Injectable } from '@angular/core';

import { ComponentBase } from '../component-base';
import { Select3dService } from './select-3d.service';
import { StageService } from './stage.service';
import { SceneUtilsService } from './scene-utils.service';
import { EventLogService, Actions } from './eventLog';
import { Subscription } from 'rxjs';
import { WallResizeControls } from '../custom/Controls';
import { WorldSpaceOffsetCalculator } from '../Component(2D and 3D)/Shared/ObjectResize/WorldSpaceOffsetCalculator';
import {
  ISizeCalculator,
  NewSizeCalculator
} from '../Component(2D and 3D)/Shared/ObjectResize/NewSizeCalculator';
import { ScaleInfo } from '../Component(2D and 3D)/Shared/ObjectResize/ScaleInfo';
import { WallResizer } from '../Component(2D and 3D)/Shared/ObjectResize/WallResizer';
import { OrbitService } from './orbit.service';
import {
  WallChildrenJutOutValidator,
  WallSizeValidator,
  WallNeighbourTierRestrictionValidator,
  IValidator
} from '../Component(2D and 3D)/Shared/ObjectResize/Validators';
import { SetWallMaterialService } from './set-wall-material.service';
import { RailingGenerationService } from './railing/services/railing-generation.service';

declare const THREE: any;

@Injectable()
export class DragWallEdgesService extends ComponentBase {
  private wallConfig: {};
  private raycaster = new THREE.Raycaster();
  private selectedWall;
  private selectWallSubscription: Subscription | null = null;
  private resizeControls = new WallResizeControls();
  private isResizing = false;
  private offsetCalculator = new WorldSpaceOffsetCalculator();
  private scaledSizeCalculator = NewSizeCalculator.Create();
  private newSizeCalculator: ISizeCalculator;
  private scaleInfo: ScaleInfo = new ScaleInfo();
  private wallResizer: WallResizer;
  private wallPosition = new THREE.Vector3();
  private lastValidPosition = new THREE.Vector3();
  private validators: IValidator[];

  constructor(
    private stage: StageService,
    private sceneUtils: SceneUtilsService,
    private select3dService: Select3dService,
    private eventLog: EventLogService,
    private orbitControl: OrbitService,
    private wallMaterialSrvc: SetWallMaterialService,
    private railingGenerationSrvc: RailingGenerationService
  ) {
    super();
    this.disable();
    this.wallResizer = new WallResizer();
    this.selectWallSubscription = select3dService.selectedWallSubject.subscribe(wall => {
      if (this.isEnabled()) {
        this.stage.add(this.resizeControls);
        this.resizeControls.visible = true;
        this.resizeControls.update(wall);
        this.selectedWall = wall;
      }
    });
    this.validators = [
      new WallSizeValidator(),
      new WallChildrenJutOutValidator(this.scaleInfo),
      new WallNeighbourTierRestrictionValidator(this.scaleInfo)
    ];
  }

  private static calculateDistance(wall): number {
    const wallConfig = wall.userData.config;
    const wallCenter = wall.localToWorld(new THREE.Vector3()).setY(0);
    const edgeStart = new THREE.Vector3(wallConfig.firstPoint.x, 0, wallConfig.firstPoint.y);
    const edgeEnd = new THREE.Vector3(wallConfig.secondPoint.x, 0, wallConfig.secondPoint.y);
    const edgeDirection = edgeEnd.sub(edgeStart);
    const wallCenterDirection = wallCenter.clone().sub(edgeStart);
    const isOppsiteDir = edgeDirection.dot(wallCenterDirection) < 0;
    return wallCenter.distanceTo(edgeStart) * (isOppsiteDir ? -1 : 1);
  }

  mouseDown(e: MouseEvent) {
    if (this.resizeControls.visible) {
      this.sceneUtils.getRay(e.offsetX, e.offsetY, this.raycaster);
      const intersection = this.raycaster.intersectObjects(this.resizeControls.children);

      if (intersection.length !== 0) {
        this.isResizing = true;
        const { scaleInfo, selectedWall: wall } = this;
        const wallConfig = wall.userData.config;
        const button = intersection[0].object;
        const { width: length, height } = wallConfig.firstPoint;

        scaleInfo.calculate(wall, button.name);

        this.orbitControl.disable();

        wall.getWorldPosition(this.wallPosition);
        this.lastValidPosition.copy(this.wallPosition);

        const offset = this.offsetCalculator.calculate(scaleInfo, length / 2, height / 2).negate();

        this.newSizeCalculator = this.scaledSizeCalculator
          .setObjectWorldPosition(this.wallPosition)
          .setOffset(offset)
          .setNormal(scaleInfo.planeNormal);

        this.wallConfig = {
          ...wallConfig,
          firstPoint: { ...wallConfig.firstPoint }
        };

        return false;
      } else {
        this.resizeControls.visible = false;
      }
    }
  }

  private canResize(wall, newSize, newPosition): boolean {
    const newDimensions = new THREE.Vector3(newSize.length, newSize.height, newSize.depth);
    return (
      this.validators.find(validator => !validator.validate(wall, newDimensions, newPosition)) ===
      undefined
    );
  }

  mouseMove(e: MouseEvent) {
    if (this.isResizing && this.resizeControls.visible) {
      this.sceneUtils.getRay(e.offsetX, e.offsetY, this.raycaster);
      const { selectedWall, scaleInfo } = this;
      const wallConfig = selectedWall.userData.config;
      const { width: length, height, depth: width } = wallConfig.firstPoint;
      const newSize = this.newSizeCalculator.calculate(
        this.raycaster.ray,
        scaleInfo.worldScaleDirection
      );

      const currentSize = {
        length,
        height
      };

      const newObjectSize = {
        length: scaleInfo.localScaleDirection.x !== 0 ? newSize : length,
        height: scaleInfo.localScaleDirection.y !== 0 ? newSize : height,
        depth: width
      };

      const offset = this.offsetCalculator.calculate(
        scaleInfo,
        newObjectSize.length - currentSize.length,
        newObjectSize.height - currentSize.height
      );

      this.wallPosition.copy(this.lastValidPosition).add(offset);

      if (this.canResize(selectedWall, newObjectSize, this.wallPosition)) {
        this.wallResizer.resize({
          object: selectedWall,
          newSize: newObjectSize,
          scaleInfo,
          currentSize: { length, height },
          includeChildren: false
        });

        selectedWall.updateMatrixWorld();
        selectedWall.getWorldPosition(this.lastValidPosition);

        wallConfig.height = wallConfig.firstPoint.height = newObjectSize.height;
        wallConfig.width = wallConfig.firstPoint.width = newObjectSize.length;
        wallConfig.firstPoint.distance = wallConfig.distance = DragWallEdgesService.calculateDistance(
          selectedWall
        );

        this.wallMaterialSrvc.changeSelectedWallMaterial(wallConfig.material, selectedWall);
        this.resizeControls.update(selectedWall);
      }

      return false;
    }
  }

  mouseUp() {
    if (this.isResizing) {
      this.orbitControl.enable();
      this.railingGenerationSrvc.buildRailing(this.railingGenerationSrvc.IsVisible());
    }
    this.isResizing = false;
    if (this.wallConfig) {
      this.eventLog.dispatch(new Actions.ChangeWallSizeAction(this.wallConfig));
      this.wallConfig = null;
    }
  }

  disable() {
    this.resizeControls.visible = false;
    super.disable();
  }
}



// WEBPACK FOOTER //
// ./src/app/services/drag-wall-edges.service.ts